import React, { useEffect, useState } from "react";
import {
  getUserTransactions,
  getMainWalletBalance,
} from "../../../../utils/api";
import { formatDateTime } from "../../../../utils/common";
import { CsvDownApi } from "../../../../v2/integrations/export";
import config from "../../../../utils/config";
import { Table } from "../../../../v2/design-system";
import { HubFlexRow } from "../societyHubShell";
import { theme } from "../../../../v2/design-system/theme";
import { v2PaginationOneBased } from "../tableShared";

const UserTransactionsTable = ({ user }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [walletBalance, setWalletBalance] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletError, setWalletError] = useState(false);

  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  useEffect(() => {
    if (!user?.id) return;

    const fetchTransactions = async () => {
      setLoading(true);
      try {
        const payload = {
          userId: user.id,
          pagination: {
            pageNo: page,
            pageSize: itemsPerPage,
          },
        };
        const res = await getUserTransactions(payload);
        const data = res?.data?.data;
        setTransactions(data?.content || []);
        setTotalRecords(data?.totalElements || 0);
      } catch (error) {
        console.error("Failed to fetch transactions:", error);
        setTransactions([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [user?.id, page, itemsPerPage]);

  useEffect(() => {
    if (!user?.id) return;

    const fetchWalletBalance = async () => {
      setWalletLoading(true);
      setWalletError(false);

      try {
        const res = await getMainWalletBalance(user.id);

        if (res?.data?.code === "0000") {
          setWalletBalance(res?.data?.data ?? 0);
        } else {
          setWalletBalance(null);
          setWalletError(true);
        }
      } catch (error) {
        console.error("Failed to fetch main wallet balance:", error);
        setWalletBalance(null);
        setWalletError(true);
      } finally {
        setWalletLoading(false);
      }
    };

    fetchWalletBalance();
  }, [user?.id]);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) {
      return "";
    }

    return `₹${Number(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const columns = [
    {
      key: "transactionId",
      label: "Transaction ID",
      align: "left",
    },
    {
      key: "transactionDate",
      label: "Transaction Date",
      align: "left",
      render: (_, row) => formatDateTime(row?.transactionDate),
    },
    {
      key: "transationType",
      label: "Type",
      align: "left",
    },
    {
      key: "baseAmount",
      label: "Amount",
      align: "left",
      render: (_, row) =>
        row?.baseAmount !== undefined && row?.baseAmount !== null
          ? formatCurrency(row.baseAmount)
          : "",
    },
    {
      key: "txnStatus",
      label: "Status",
      align: "left",
    },
    {
      key: "sourceUserName",
      label: "Source User",
      align: "left",
    },
    {
      key: "destinationUserName",
      label: "Destination User",
      align: "left",
    },
    {
      key: "subTransactionMode",
      label: "Mode of Transaction",
      align: "left",
    },
  ];

  const csvHeaders = columns.map(({ key, label }) => ({
    key,
    label,
  }));

  const csvParams = {
    userId: user.id,
    pagination: {
      pageNo: page,
      pageSize: itemsPerPage,
    },
  };

  const pagination =
    totalRecords > 0
      ? v2PaginationOneBased({
          pageOneBased: page,
          pageSize: itemsPerPage,
          total: totalRecords,
          setPageOneBased: setPage,
          setPageSize: setItemsPerPage,
        })
      : undefined;

  return (
    <>
      <div style={styles.walletCard}>
        <div style={styles.walletTop}>
          <div style={styles.walletIcon}>
            <span style={styles.walletIconText}>₹</span>
          </div>

          <div>
            <div style={styles.walletTitle}>MAIN WALLET</div>
            <div style={styles.walletSubtitle}>
              Available Balance
            </div>
          </div>
        </div>

        <div style={styles.walletBalance}>
          {walletLoading ? (
            <div style={styles.balanceSkeleton} />
          ) : walletError ? (
            <span style={styles.errorText}>
              Not Available
            </span>
          ) : (
            formatCurrency(walletBalance)
          )}
        </div>
      </div>

      <HubFlexRow
        $justify="flex-end"
        $mb={theme.spacing.md}
      >
        {transactions.length > 0 && (
          <CsvDownApi
            headers={csvHeaders}
            url={`${config.apis.societyPartnerHub.USER_TRANSACTIONS}`}
            params={csvParams}
            method="post"
            reportName={`${user?.name || "User"}_Transactions.csv`}
            title="Download CSV"
          />
        )}
      </HubFlexRow>
      <Table
        data={transactions}
        columns={columns}
        rowKey="transactionId"
        loading={loading}
        emptyMessage="No transactions"
        pagination={pagination}
      />
    </>
  );
};

const styles = {
  walletCard: {
    width: "360px",
    minHeight: "115px",
    padding: "18px 20px",
    marginBottom: "24px",
    backgroundColor: "#ffffff",
    border: "1px solid #ddd6fe",
    borderRadius: "14px",
    boxShadow: "0 2px 8px rgba(76, 29, 149, 0.08)",
    boxSizing: "border-box",
  },

  walletTop: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },

  walletIcon: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    backgroundColor: "#f3e8ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  walletIconText: {
    fontSize: "17px",
    fontWeight: 700,
    color: "#4c1d95",
  },

  walletTitle: {
    fontSize: "13px",
    fontWeight: 700,
    color: "#1f2937",
    letterSpacing: "0.3px",
  },

  walletSubtitle: {
    marginTop: "3px",
    fontSize: "13px",
    color: "#6b7280",
  },

  walletBalance: {
    marginTop: "12px",
    fontSize: "25px",
    lineHeight: "30px",
    fontWeight: 700,
    color: "#4c1d95",
  },

  balanceSkeleton: {
    width: "110px",
    height: "27px",
    borderRadius: "6px",
    backgroundColor: "#eeeeee",
  },

  errorText: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#6b7280",
  },
};

export default UserTransactionsTable;